import React, { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { UserPlus } from "lucide-react";

export default function SignupPage() {
  const { user, customerRegister } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    email: "", password: "", name: "", phone: "", shipping_address: "",
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user && user.role === "customer") return <Navigate to="/account" replace />;
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    const res = await customerRegister(form);
    setSubmitting(false);
    if (res.ok) navigate("/account");
    else setError(res.error || "Registration failed");
  };

  return (
    <div data-testid="signup-page" className="max-w-xl mx-auto px-4 py-16">
      <div className="text-xs tracking-[0.25em] uppercase font-mono text-yellow-400 mb-3">// Create account</div>
      <h1 className="font-[Outfit] text-4xl sm:text-5xl font-black tracking-tighter">Join the Arena.</h1>
      <p className="mt-3 text-slate-400">
        Create an account so your info is saved for every future order — no more re-typing.
      </p>

      <form onSubmit={submit} className="mt-10 bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full name" required>
            <input data-testid="signup-name" required value={form.name} onChange={(e) => setField("name", e.target.value)} className={input} />
          </Field>
          <Field label="Email" required>
            <input data-testid="signup-email" type="email" required value={form.email} onChange={(e) => setField("email", e.target.value)} className={input} />
          </Field>
          <Field label="Password (min 6)" required>
            <input data-testid="signup-password" type="password" required minLength={6} value={form.password} onChange={(e) => setField("password", e.target.value)} className={input} />
          </Field>
          <Field label="Phone (optional)">
            <input data-testid="signup-phone" value={form.phone} onChange={(e) => setField("phone", e.target.value)} className={input} />
          </Field>
        </div>
        <Field label="Default shipping address (optional)">
          <textarea data-testid="signup-address" rows={3} value={form.shipping_address} onChange={(e) => setField("shipping_address", e.target.value)} className={input} placeholder="Street, city, province, postal code" />
        </Field>

        {error && (
          <div data-testid="signup-error" className="text-red-400 bg-red-500/10 border border-red-500/30 px-3 py-2 rounded text-sm">{error}</div>
        )}

        <button data-testid="signup-submit" type="submit" disabled={submitting} className="w-full bg-yellow-400 text-slate-950 font-bold px-4 py-3 rounded-md uppercase tracking-wide hover:bg-yellow-300 disabled:opacity-50">
          <UserPlus className="w-4 h-4 inline mr-1" /> {submitting ? "Creating…" : "Create account"}
        </button>
        <div className="text-xs text-center text-slate-500">
          Already have an account?{" "}
          <Link to="/signin" className="text-yellow-400 hover:text-yellow-300">Sign in</Link>
        </div>
      </form>
    </div>
  );
}

const input = "w-full bg-slate-950 border border-slate-800 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-yellow-400";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </span>
      {children}
    </label>
  );
}
